import React, {useCallback, useMemo, useState} from "react"
import WhiteLogoImage from "../../assets/icons/ngs-white-blue.svg"
import {useNavigate, useParams} from "react-router-dom";
import {apiV2, massWarningAPI} from "../../api/axiosConfig";
import {useQuery} from "react-query";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import {ButtonV2} from "../../components/ui/ButtonsV2/ButtonV2";
import clsx from "clsx";
import BasicText from "../../components/form-v2/basic-text";
import {PhoneInput} from "../../components/form-v2/phone-input/PhoneInput";
import {isAxiosError} from "axios";
import {ErrorMessage} from "../siren/ErrorMessage";

const Layout: React.FC<React.PropsWithChildren> = (props) => {


    return (
        <div className={"flex flex-col items-center justify-start gap-8 text-grey bg-desaturated-grey min-h-screen"}>
            <Header/>
            <div className="w-full p-8 flex flex-col items-center gap-8">
                {props.children}
            </div>
        </div>
    )

}

const Header: React.FC = () => {


    return <div
        className="bg-white bg-gradient-to-r from-[#2D166F_50%] to-[#007584] h-28 w-full px-6 flex justify-center">
        <img
            src={WhiteLogoImage}
            alt="NGS Logo"
            className="h-fill w-auto max-h-30 max-w-[200px] object-contain"
        />
    </div>

}

type MassWarningData = {
    completed: boolean
    responseOptions: { index: number, text: string }[]
}

async function fetchData(id: string) {
    const res = await apiV2.get<MassWarningData>(`mass-warning-alerts/response-options/${id}`, {})
    return res.data;
}

function Inner({completed, responseOptions}: MassWarningData) {

    const {id} = useParams()

    const [selected, setSelected] = useState<number>()
    const [loading, setLoading] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    const [error, setError] = useState<string>()

    const submit = useCallback(() => {
        setLoading(true)

        massWarningAPI.post(`recipient/${id}/respond`, {index: selected}).then(() => {
            setLoading(false)
            setSubmitted(true)
        }).catch(() => {
            setError("Error submitting response")
        })

        setTimeout(() => {
            setLoading(false);
            setSubmitted(true)
        }, 2000)
    }, [selected, id])

    const style = useCallback((index: number) => {
        return clsx(
            "px-4 py-2 rounded-md bg-white shadow-md text-left flex gap-4 w-[400px] max-w-full",
            {
                "outline outline-light-grey outline-1": index != selected,
                "outline outline-blue outline-2": index == selected,
            }
        )
    }, [selected])



    if (submitted) {
        return <p className="text-center">We have received your response.</p>
    }

    if (completed) {
        return <p className="text-center">This alert has already been responded to.</p>
    }

    if (error) {
        return <p className="text-center text-red">{error}</p>
    }


    return (
        <div className={"grid gap-4 max-w-[400px]"}>
            <p className="text-center w-full">You have been asked to confirm your status by NGS on behalf
                of your company.</p>
            <p className="text-center w-full">Please select the option below and submit.</p>
            {responseOptions.map((option, i) => (
                <div key={i}>
                    <button className={style(option.index) + " w-full"} onClick={() => setSelected(option.index)}>
                        <div>
                            {option.index === selected ? (
                                <RadioButtonCheckedIcon className={"text-blue"}/>
                            ) : (
                                <RadioButtonUncheckedIcon/>
                            )}
                        </div>
                        <div>
                            {option.index}

                        </div>
                        <div className="flex-grow text-right">
                            {option.text}
                        </div>
                    </button>
                </div>
            ))}

            <ButtonV2 label={"Submit"} disabled={!selected} onClick={submit} loading={loading}/>


        </div>
    )


    return null

}


export function MassWarningResponsePage() {

    const {id} = useParams()

    const {data, isLoading, isError, error} = useQuery({
        queryKey: ["response-options", id],
        queryFn: () => fetchData(id!),
        enabled: !!id,
    });

    const content = useMemo(() => {

        if (!id) {
            return <SearchPage/>
        }

        if (isLoading) {
            return <p>Loading...</p>
        }
        if (isError) {
            return <p>An error occurred</p>
        }
        if (data) return <Inner {...data}/>


    }, [id, data, isLoading, isError, error]);



    return (
        <Layout>
            {content}
        </Layout>
    )
}

function SearchPage() {

    const navigate = useNavigate()
    const [mode, setMode] = useState<"email" | "mobile">("email")

    const [email, setEmail] = useState<string>("")
    const [mobile, setMobile] = useState<string>("")


    const [loading, setLoading] = useState(false)

    const [error, setError] = useState<string>()


    const submit = useCallback(async () => {
        setLoading(true)
        setError(undefined)
        try {
            const query = mode === "email" ? email : mobile
            const res = await apiV2.get<{id: string}>("mass-warning-alerts/find-recipient", {
                params: {
                    query: query,
                }
            })

            if(res.data.id){
                navigate(`/mass-warning-response/${res.data.id}`)
                return
            } else {
                setError("An error has occurred")
            }

        } catch (error){
            if(isAxiosError(error) && error?.response?.data?.error){
                setError(error.response.data.error)
            } else {
                setError("An error has occurred")
            }
        }

        setLoading(false)




    }, [email, mobile, mode])


    return (
        <div className={"flex flex-col gap-4"}>
            <div className="flex flex-row items-center justify-center gap-8">
                <button className={mode != "email" ? "opacity-50" : ""} onClick={() => setMode("email")}>Email Address
                </button>
                <button className={mode != "mobile" ? "opacity-50" : ""} onClick={() => setMode("mobile")}>Mobile
                    Phone
                </button>
            </div>
            {mode == "email" && (<BasicText value={email} onChange={setEmail} placeholder={"Email Address"}/>)}
            {mode == "mobile" && (
                <PhoneInput initialValue={mobile} onChange={setMobile} placeholder={"Phone number"}/>)}

            <ButtonV2 label={"Submit"} onClick={submit} loading={loading}/>
            <ErrorMessage text={error} />
        </div>
    )
}